<template>
  <NForm class="content-selector-pexels-images h-full flex flex-col">
    <NGrid class="h-full overflow-hidden mt-3" cols="12" item-responsive>
      <NGridItem class="h-full overflow-auto" :span="'12 650:7 1200:8 1400:9'">
        <div class="mr-3 h-full">
          <div class="flex flex-col gap-3 mb-3">
            <NFormItem
              :label="t('canvasUI.contentSelector.pexelsImages.searchPrompt')"
              path="query"
              :rule="{
                trigger: ['input', 'blur'],
              }"
            >
              <NInput
                v-model:value="searchQuery"
                clearable
                :disabled="isLoading"
                placeholder=""
                @keyup.enter="onSearch"
              />
            </NFormItem>

            <NFormItem :label="t('canvasUI.canvasBuilder.multimediaSettings.chooseMediaType')" path="mediaType">
              <NSelect
                v-model:value="mediaType"
                :disabled="isLoading"
                :options="[
                  { label: t('canvasUI.canvasBuilder.multimediaSettings.image'), value: 'image' },
                  { label: t('canvasUI.canvasBuilder.multimediaSettings.video'), value: 'video' },
                ]"
              />
            </NFormItem>
          </div>

          <div class="flex justify-between w-full mb-3">
            <div v-if="error" class="text-error text-sm">{{ error }}</div>
            <div class="flex-grow"></div>
            <CButton :disabled="isLoading || !searchQuery" :loading="isLoading" type="primary" @click="onSearch">
              {{ t('canvasUI.contentSelector.pexelsImages.search') }}
            </CButton>
          </div>

          <div v-if="isLoading" class="text-center py-5">
            <NSpin />
            <div class="mt-2">{{ t('canvasUI.contentSelector.pexelsImages.loadingMessage') }}</div>
          </div>

          <div v-else-if="searchResults.length === 0 && hasSearched" class="text-center py-5">
            {{ t('canvasUI.contentSelector.pexelsImages.noResults') }}
          </div>

          <div v-else class="pexels-grid">
            <div v-for="item in searchResults" :key="item.id" class="pexels-item">
              <div class="pexels-preview" @click="onSelectMedia(item)">
                <img
                  v-if="item.type === 'image'"
                  alt="Pexels image"
                  class="pexels-thumbnail"
                  :src="item.thumbnailUrl"
                />
                <div v-else-if="item.type === 'video'" class="pexels-video-thumbnail">
                  <img alt="Video thumbnail" class="pexels-thumbnail" :src="item.thumbnailUrl" />
                  <div class="pexels-play-overlay">
                    <CIcon icon="play-circle" size="32" />
                  </div>
                </div>
                <div class="pexels-item-overlay">
                  <CButton class="pexels-select-btn" size="small" type="primary" @click.stop="onSelectMedia(item)">
                    {{ t('canvasUI.common.select') }}
                  </CButton>
                </div>
              </div>
              <div class="text-xs-black mt-1 truncate">{{ item.title || 'Untitled' }}</div>
              <div class="text-xs text-gray-500">{{ item.photographer }}</div>
            </div>
          </div>

          <div v-if="searchResults.length > 0 && hasMoreResults" class="text-center mt-4">
            <CButton :loading="isLoadingMore" secondary @click="loadMore">
              {{ t('canvasUI.contentSelector.pexelsImages.loadMore') }}
            </CButton>
          </div>
        </div>
      </NGridItem>
      <NGridItem class="h-full overflow-auto" :span="'12 650:5 1200:4 1400:3'">
        <SelectionPanel />
      </NGridItem>
    </NGrid>
  </NForm>
</template>

<script setup lang="ts">
import { NGridItem, NGrid, NFormItem, NInput, NForm, NSelect, NSpin } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { computed, ref, inject, watch, type ComputedRef } from 'vue'
import axios from 'axios'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import SelectionPanel from '@content-selector/components/Content/SelectionPanel/SelectionPanel.vue'
import { type PitcherInfo } from '@lib/types/app'
import useThemeVars from '@lib/composables/useThemeVars'

interface PexelsImage {
  id: number
  title: string
  photographer: string
  type: 'image'
  url: string
  thumbnailUrl: string
}

interface PexelsVideo {
  id: number
  title: string
  photographer: string
  type: 'video'
  url: string
  thumbnailUrl: string
}

type PexelsMedia = PexelsImage | PexelsVideo

const emit = defineEmits<{
  add: [{ url: string; content_type: 'image' | 'video' }]
}>()

const { t } = useI18n()

const pitcherInfo = inject<ComputedRef<Partial<PitcherInfo>>>(
  'pitcherInfo',
  computed(() => ({ apiOrigin: '' })),
)

const mediaType = ref<'image' | 'video'>('image')
const searchQuery = ref('')
const searchResults = ref<PexelsMedia[]>([])
const isLoading = ref(false)
const isLoadingMore = ref(false)
const error = ref('')
const currentPage = ref(1)
const hasMoreResults = ref(false)
const hasSearched = ref(false)
const themeVars = useThemeVars()

// API endpoints for the Next Core Pexels proxy
const pexelsApiBaseUrl = computed(() => `${pitcherInfo.value?.apiOrigin}/core/api/protected/pexels`)

watch(mediaType, () => {
  // Reset results when switching media type
  searchResults.value = []
  currentPage.value = 1
  hasMoreResults.value = false
  hasSearched.value = false
})

async function fetchPexelsMedia(page: number = 1) {
  try {
    const endpoint = mediaType.value === 'image' ? '/search' : '/videos/search'
    const response = await axios.get(`${pexelsApiBaseUrl.value}${endpoint}`, {
      headers: {
        Authorization: `Bearer ${pitcherInfo.value?.accessToken}`,
      },
      params: {
        query: searchQuery.value,
        per_page: 20,
        page: page,
      },
    })

    const data = response.data

    if (mediaType.value === 'image') {
      // Handle image results
      const images = data.photos.map(
        (photo: any): PexelsImage => ({
          id: photo.id,
          title: photo.alt || 'Pexels Image',
          photographer: photo.photographer,
          type: 'image',
          url: photo.src.original,
          thumbnailUrl: photo.src.medium,
        }),
      )

      return {
        items: images,
        hasMore: data.total_results > page * 20,
      }
    } else {
      // Handle video results
      const videos = data.videos.map(
        (video: any): PexelsVideo => ({
          id: video.id,
          title: video.user.name ? `Video by ${video.user.name}` : 'Pexels Video',
          photographer: video.user.name,
          type: 'video',
          url: video.video_files[0].link, // Get the highest quality video file
          thumbnailUrl: video.image,
        }),
      )

      return {
        items: videos,
        hasMore: data.total_results > page * 20,
      }
    }
  } catch (e) {
    console.error(`Error fetching Pexels ${mediaType.value}s:`, e)
    throw e
  }
}

async function onSearch() {
  if (!searchQuery.value) return

  try {
    isLoading.value = true
    error.value = ''
    currentPage.value = 1

    const result = await fetchPexelsMedia(1)
    searchResults.value = result.items
    hasMoreResults.value = result.hasMore
    hasSearched.value = true
  } catch (e) {
    error.value = e instanceof Error ? e.message : `Failed to search Pexels ${mediaType.value}s`
    searchResults.value = []
    hasMoreResults.value = false
  } finally {
    isLoading.value = false
  }
}

async function loadMore() {
  if (isLoadingMore.value) return

  try {
    isLoadingMore.value = true
    const nextPage = currentPage.value + 1

    const result = await fetchPexelsMedia(nextPage)
    searchResults.value = [...searchResults.value, ...result.items]
    hasMoreResults.value = result.hasMore
    currentPage.value = nextPage
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load more results'
  } finally {
    isLoadingMore.value = false
  }
}

function onSelectMedia(item: PexelsMedia) {
  emit('add', {
    url: item.url,
    content_type: item.type,
  })
}
</script>

<style lang="scss" scoped>
.pexels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.pexels-item {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition:
    transform 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;
  background-color: v-bind('themeVars.primary6');
  padding: 12px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

    .pexels-item-overlay {
      opacity: 1;
    }
  }
}

.pexels-preview {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;

  &:hover {
    .pexels-item-overlay {
      opacity: 1;
    }
  }
}

.pexels-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.pexels-video-thumbnail {
  position: relative;
}

.pexels-play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  opacity: 0.9;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
}

.pexels-item-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.pexels-select-btn {
  transform: translateY(0);
  transition: transform 0.2s ease-in-out;
  z-index: 2;

  .pexels-item-overlay:hover & {
    transform: translateY(-4px);
  }
}

.text-xs-black {
  font-weight: 700;
  text-align: center;
  color: v-bind('themeVars.text2');
}
.text-xs {
  font-weight: 700;
  text-align: center;
  color: v-bind('themeVars.text3');
}
</style>
